<template>
  <section id="news" class="news-section">
    <div class="container">
      <div class="block-header center">
        <span class="block-tag">{{ locale === 'zh' ? '新闻资讯' : 'News' }}</span>
        <h2 class="block-title">{{ locale === 'zh' ? '新闻资讯' : 'Latest News' }}</h2>
        <p class="block-desc">{{ locale === 'zh' ? '了解公司最新动态和行业资讯' : 'Stay updated with our latest news' }}</p>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ locale === 'zh' ? '加载中...' : 'Loading...' }}</p>
      </div>

      <div v-else-if="newsList.length === 0" class="empty-state">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
        </svg>
        <h3>{{ locale === 'zh' ? '暂无新闻' : 'No News' }}</h3>
        <p>{{ locale === 'zh' ? '新闻资讯正在更新中，敬请期待' : 'News coming soon' }}</p>
      </div>

      <div v-else class="news-grid">
        <article
          v-for="item in newsList"
          :key="item.id"
          class="news-card"
          @click="goToDetail(item.id)"
        >
          <div class="news-card-image" v-if="item.coverImageUrl">
            <img :src="getImageFullUrl(item.coverImageUrl)" :alt="locale === 'zh' ? item.titleZh : item.titleEn" @error="handleImageError">
            <div class="news-card-date">
              <span class="date-day">{{ formatDay(item.createdAt) }}</span>
              <span class="date-month">{{ formatMonth(item.createdAt) }}</span>
            </div>
          </div>
          <div class="news-card-body" :class="{ 'no-image': !item.coverImageUrl }">
            <div class="news-card-date-alt" v-if="!item.coverImageUrl">
              <span class="date-day">{{ formatDay(item.createdAt) }}</span>
              <span class="date-month">{{ formatMonth(item.createdAt) }}</span>
            </div>
            <h3 class="news-card-title">{{ locale === 'zh' ? item.titleZh : item.titleEn }}</h3>
            <p class="news-card-summary">{{ locale === 'zh' ? (item.summaryZh || '') : (item.summaryEn || '') }}</p>
            <div class="news-card-footer">
              <span v-if="item.author" class="news-card-author">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {{ item.author }}
              </span>
              <span class="news-card-readmore">
                {{ locale === 'zh' ? '阅读更多' : 'Read More' }}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </span>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { getImageFullUrl } from '../utils/image'

export default {
  name: 'NewsSection',
  data() {
    return {
      newsList: [],
      loading: true
    }
  },
  computed: {
    locale() {
      return this.$i18n ? this.$i18n.locale : (localStorage.getItem('locale') || 'zh')
    }
  },
  mounted() {
    this.fetchNews()
  },
  methods: {
    getImageFullUrl(url) {
      return getImageFullUrl(url)
    },
    handleImageError(e) {
      e.target.parentElement.classList.add('image-error')
    },
    async fetchNews() {
      try {
        const res = await axios.get('/api/news/active')
        this.newsList = res.data || []
      } catch (e) {
        console.error('Failed to load news:', e)
      } finally {
        this.loading = false
      }
    },
    goToDetail(id) {
      this.$router.push({ name: 'NewsDetail', params: { id } })
    },
    formatDay(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return String(d.getDate()).padStart(2, '0')
    },
    formatMonth(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return months[d.getMonth()]
    }
  }
}
</script>

<style scoped>
.news-section {
  padding: 70px 0;
  background: var(--cream, #f8f6f0);
}

.block-header {
  text-align: center;
  margin-bottom: 44px;
}

.block-tag {
  display: inline-block;
  background: var(--sage, #7a8e6a);
  color: #fff;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
}

.block-title {
  font-size: 36px;
  color: var(--bark, #3d3d3d);
  margin-bottom: 16px;
  font-weight: 700;
}

.block-desc {
  font-size: 16px;
  color: var(--stone, #666);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--stone, #666);
}

.empty-state svg {
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-state h3 {
  font-size: 20px;
  color: var(--bark, #3d3d3d);
  margin-bottom: 8px;
}

/* 新闻网格 */
.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.news-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid var(--sand, #e8e4d9);
  display: flex;
  flex-direction: column;
}

.news-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.1);
  border-color: var(--sage, #7a8e6a);
}

.news-card-image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: var(--sand, #e8e4d9);
}

.news-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.news-card:hover .news-card-image img {
  transform: scale(1.05);
}

.news-card-date {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(255,255,255,0.95);
  border-radius: 8px;
  padding: 6px 12px;
  text-align: center;
  line-height: 1.2;
  min-width: 48px;
}

.date-day {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: var(--sage-dark, #5c6b50);
}

.date-month {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--stone, #666);
  text-transform: uppercase;
}

.news-card-body {
  padding: 20px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.news-card-body.no-image {
  padding-top: 24px;
}

.news-card-date-alt {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.news-card-date-alt .date-day {
  font-size: 28px;
}

.news-card-date-alt .date-month {
  font-size: 13px;
}

.news-card-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--bark, #3d3d3d);
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-card-summary {
  font-size: 14px;
  color: var(--stone, #666);
  line-height: 1.6;
  margin-bottom: 16px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 14px;
  border-top: 1px solid var(--sand, #e8e4d9);
}

.news-card-author {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--stone, #666);
}

.news-card-author svg {
  opacity: 0.6;
}

.news-card-readmore {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: var(--sage, #7a8e6a);
  transition: gap 0.3s ease;
}

.news-card:hover .news-card-readmore {
  gap: 8px;
}

.image-error {
  background: var(--sand, #e8e4d9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-error::after {
  content: '';
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0,0,0,0.05);
}

/* 响应式 */
@media (max-width: 1024px) {
  .news-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .news-grid {
    grid-template-columns: 1fr;
  }

  .news-section {
    padding: 50px 0;
  }

  .block-title {
    font-size: 28px;
  }

  .news-card-image {
    height: 180px;
  }
}
</style>